/**
 * MemberProfileCompletion — branche l'accueil du chatteur (`AccueilDuChatteur`)
 * sur Convex. Affiché par MemberProfileGate à un membre INVITÉ tant que :
 *
 *   1. son Discord n'est pas vérifié — OAuth (`identify` +
 *      `guilds.members.read`), qui prouve qu'il possède le compte ET qu'il est
 *      présent dans le serveur Dozka⚔ (/discord/callback → discordVerifiedAt).
 *      **C'est la seule exigence bloquante.**
 *   2. OU il n'a pas encore CONFIRMÉ son accueil (`accueilAFaire`) : photo,
 *      pseudo, thème, fond, taille — puis « Entrer dans le cockpit »
 *      (`members.terminerMonAccueil`). Ajouté le 07-10-2026.
 *
 * 🩸 L'adresse USDC ne bloque pas, depuis le 19-08-2026. Décision de Kayla :
 * « vérifier l'adresse USDC avant d'entrer, j'aimerais que ce soit pas
 * obligatoire […] par contre il est obligé de faire le truc Discord ». Le
 * changement vit dans `members.myProfileGate`, pas ici : cet écran ne décide
 * rien, il affiche. L'adresse se pose aussi ensuite depuis Organisation ›
 * Paiements (`WalletTab`, self-service).
 *
 * 🪟 Fenêtre de déploiement : un Convex qui ne connaît pas encore
 * `accueilAFaire` le laisse `undefined` — l'écran se démonte alors dès
 * Discord, comme avant.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { Button, Carte } from "@/shared/ui";
import { AlertCircle, AlertTriangle, DiscordLogo, Loader2, Link } from "@/shared/ui/icons";
import { WalletAddressEditor } from "@/panels/organisation/WalletAddressEditor";
import { useSession } from "./useSession";
import { AccueilDuChatteur } from "./accueil/AccueilDuChatteur";

export interface ProfileGate {
  requiresCompletion: boolean;
  membershipId: Id<"accountMembers"> | null;
  hasWallet: boolean;
  hasDiscordVerified: boolean;
  discordUsername: string | null;
  discordAvatarUrl?: string | null;
  /** Absent tant que Convex n'est pas déployé avec l'accueil. */
  accueilAFaire?: boolean;
}

interface Props {
  accountId: Id<"accounts">;
  membershipId: Id<"accountMembers">;
  gate: ProfileGate;
  onSignOut: () => void;
}

/** Statut renvoyé par /discord/callback via le query param `?discord=`. */
type DiscordReturn = "ok" | "notinserver" | "dejapris" | "expired" | "error" | null;

function readDiscordReturn(): DiscordReturn {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("discord");
  if (v === "ok" || v === "notinserver" || v === "dejapris" || v === "expired" || v === "error") {
    return v;
  }
  return null;
}

export function MemberProfileCompletion({
  accountId,
  membershipId,
  gate,
  onSignOut,
}: Props) {
  const { t } = useTranslation();
  const { user } = useSession();
  const me = useQuery(api.users.me);
  const updateName = useMutation(api.users.updateName);
  const uploadAvatar = useAction(api.users.uploadAvatar);
  const clearAvatar = useAction(api.users.clearAvatar);
  const prendreMaPhotoDiscord = useAction(api.users.prendreMaPhotoDiscord);
  const terminerMonAccueil = useMutation(api.members.terminerMonAccueil);
  const startDiscordLink = useMutation(api.discordLink.startDiscordLink);
  const createServerInvite = useAction(api.discordLink.createServerInvite);

  const [connecting, setConnecting] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  // Invitation unique (générée à la demande par le bot Cerbère).
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [invitePending, setInvitePending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Statut du retour OAuth (nettoyé de l'URL au mount pour ne pas persister
  // au refresh). `notinserver`/`expired`/`error` restent affichés jusqu'à la
  // prochaine tentative.
  const [discordReturn, setDiscordReturn] = useState<DiscordReturn>(() =>
    readDiscordReturn(),
  );
  useEffect(() => {
    if (readDiscordReturn() !== null) {
      const url = new URL(window.location.href);
      url.searchParams.delete("discord");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  async function handleConnectDiscord() {
    if (connecting) return;
    setConnecting(true);
    setLinkError(null);
    setDiscordReturn(null);
    try {
      const { url } = await startDiscordLink({ accountId, retour: window.location.origin });
      window.location.href = url; // départ vers Discord (autorisation)
    } catch (err) {
      setConnecting(false);
      setLinkError(
        err instanceof Error ? err.message : String(err),
      );
    }
  }

  // Génère une invitation UNIQUE au serveur (le bot Cerbère la crée) et l'ouvre.
  async function handleGenerateInvite() {
    if (invitePending) return;
    setInvitePending(true);
    setInviteError(null);
    try {
      const { url } = await createServerInvite({});
      setInviteUrl(url);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : String(err));
    } finally {
      setInvitePending(false);
    }
  }

  const nomActuel = me?.name ?? "";
  const photoUrl = me?.avatarUrl ?? user?.profilePictureUrl ?? null;

  return (
    <AccueilDuChatteur
      discord={{
        fait: gate.hasDiscordVerified,
        pseudo: gate.discordUsername,
        avatarUrl: gate.discordAvatarUrl ?? null,
        contenu: (
          <>
            <p className="mb-4 text-[13px] leading-relaxed text-muted">
              {t("auth.profileCompletion.discord.help", {
                defaultValue:
                  "Connecte ton compte Discord : ça prouve que c'est bien toi et ça t'ajoute au serveur, où tout se passe ensuite (le test, ton shift, l'équipe).",
              })}
            </p>

            {discordReturn === "dejapris" && (
              <StatusBanner tone="warn">
                <p>
                  {t("auth.profileCompletion.discord.dejaPris", {
                    defaultValue:
                      "Ce compte Discord est déjà relié à un autre compte du cockpit. Connecte TON propre compte Discord : déconnecte-toi d'abord de Discord dans ce navigateur, puis réessaie.",
                  })}
                </p>
              </StatusBanner>
            )}

            {discordReturn === "notinserver" && (
              <StatusBanner tone="warn">
                <p className="mb-2">
                  {t("auth.profileCompletion.discord.notInServer", {
                    defaultValue:
                      "Ton compte Discord n'est pas encore dans le serveur. Génère ton invitation, rejoins, puis réessaie.",
                  })}
                </p>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => void handleGenerateInvite()}
                  disabled={invitePending}
                  icon={
                    invitePending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Link className="h-3.5 w-3.5" />
                    )
                  }
                >
                  {inviteUrl
                    ? t("auth.profileCompletion.discord.regenInvite", {
                        defaultValue: "Générer une nouvelle invitation",
                      })
                    : t("auth.profileCompletion.discord.genInvite", {
                        defaultValue: "Générer mon invitation",
                      })}
                </Button>
                {inviteUrl && (
                  <Button variant="ghost" size="md" href={inviteUrl} target="_blank" className="mt-1">
                    {t("auth.profileCompletion.discord.openInvite", {
                      defaultValue: "Ouvrir mon invitation →",
                    })}
                  </Button>
                )}
                {inviteError && (
                  <p className="mt-1 text-[12px] text-erreur">{inviteError}</p>
                )}
              </StatusBanner>
            )}
            {(discordReturn === "expired" || discordReturn === "error") && (
              <StatusBanner tone="error">
                <p>
                  {t("auth.profileCompletion.discord.retryError", {
                    defaultValue:
                      "La connexion Discord a échoué. Réessaie.",
                  })}
                </p>
              </StatusBanner>
            )}
            {linkError && (
              <StatusBanner tone="error">
                <p>{linkError}</p>
              </StatusBanner>
            )}

            <Button
              variant="accent"
              size="xl"
              onClick={() => void handleConnectDiscord()}
              disabled={connecting}
              icon={
                connecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <DiscordLogo weight="fill" className="h-4 w-4" />
                )
              }
              className="w-full"
            >
              {discordReturn === "notinserver" ||
              discordReturn === "dejapris" ||
              discordReturn === "expired" ||
              discordReturn === "error"
                ? t("auth.profileCompletion.discord.retry", {
                    defaultValue: "Réessayer",
                  })
                : t("auth.profileCompletion.discord.connect", {
                    defaultValue: "Connecter mon Discord",
                  })}
            </Button>
          </>
        ),
      }}
      paiement={{
        fait: gate.hasWallet,
        contenu: (
          <>
            <p className="mb-4 text-[13px] leading-relaxed text-muted">
              {t("auth.profileCompletion.wallet.help", {
                defaultValue:
                  "L'adresse Solana (base58) sur laquelle tu recevras tes paiements en USDC. Tu peux la renseigner plus tard, depuis Organisation › Paiements.",
              })}
            </p>
            <WalletAddressEditor
              membershipId={membershipId}
              currentWalletAddress={null}
              canEdit
            />
          </>
        ),
      }}
      profil={{
        nomActuel,
        photoUrl,
        photoPersonnelle: me?.hasCustomAvatar ?? false,
        importerPhoto: async (fichier) => {
          if (!fichier.type.startsWith("image/")) throw new Error("Ce fichier n'est pas une image");
          await uploadAvatar({ bytes: await fichier.arrayBuffer(), contentType: fichier.type });
        },
        prendrePhotoDiscord: async () => {
          await prendreMaPhotoDiscord({ accountId });
        },
        retirerPhoto: async () => {
          await clearAvatar();
        },
      }}
      entrer={async (pseudo) => {
        if (pseudo && pseudo !== nomActuel) await updateName({ name: pseudo });
        await terminerMonAccueil({ accountId });
      }}
      seDeconnecter={onSignOut}
    />
  );
}

/**
 * Bannière de statut NON-cliquable : une carte de verre, et le SENS porté par
 * l'icône seule (ambre = attention, rouge = erreur) — plus de contour coloré sur
 * fond teinté.
 */
function StatusBanner({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "error";
  children: ReactNode;
}) {
  return (
    <Carte remplissage="sm" className="mb-3" role={tone === "error" ? "alert" : undefined}>
      <div className="flex items-start gap-2.5 text-[13px] leading-relaxed text-fg">
        {tone === "error" ? (
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-erreur" aria-hidden />
        ) : tone === "warn" ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-attention" aria-hidden />
        ) : null}
        <div className="min-w-0">{children}</div>
      </div>
    </Carte>
  );
}
